import React, { useCallback, useEffect, useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import CarrierContactDrawer from './CarrierContactDrawer';
import { BoolPill } from './fields';
import { CarrierContact, listCarrierContacts } from '../../services/carriercontacts.service';

/**
 * Carrier contacts subtable for the Carrier Profiles drawer (edit mode only —
 * contacts can only be added once the carrier profile is saved). Lists contacts
 * and opens the small CarrierContactDrawer to add/edit; reloads on save.
 */
const CarrierContactsPanel: React.FC<{ carrierrecno: number }> = ({ carrierrecno }) => {
  const [contacts, setContacts] = useState<CarrierContact[]>([]);
  const [editing, setEditing] = useState<CarrierContact | null | undefined>(undefined); // undefined=closed, null=new

  const load = useCallback(() => {
    listCarrierContacts(carrierrecno).then(setContacts).catch(() => {});
  }, [carrierrecno]);

  useEffect(() => { load(); }, [load]);

  return (
    <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <legend className="px-2 text-sm font-semibold text-label">Contacts</legend>

      {contacts.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">No contacts yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted">
              <th className="py-1 font-medium">Type</th>
              <th className="py-1 font-medium">Name</th>
              <th className="py-1 font-medium">Phone</th>
              <th className="py-1 font-medium">Fax</th>
              <th className="py-1 font-medium">Email</th>
              <th className="py-1 font-medium text-center">Default</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.recno} onClick={() => setEditing(c)}
                className="border-t border-gray-100 dark:border-gray-700/60 cursor-pointer text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/40">
                <td className="py-1.5">{c.contacttype}</td>
                <td className="py-1.5">{c.contactname}</td>
                <td className="py-1.5">{c.contactphone}</td>
                <td className="py-1.5">{c.contactfax}</td>
                <td className="py-1.5">{c.contactemail}</td>
                <td className="py-1.5 text-center"><BoolPill value={c.defaultcontact === 'Y'} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <button type="button" onClick={() => setEditing(null)}
        className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
        <PlusIcon className="w-4 h-4" /> Add Contact
      </button>

      {editing !== undefined && (
        <CarrierContactDrawer
          carrierrecno={carrierrecno}
          contact={editing}
          onClose={() => setEditing(undefined)}
          onSaved={load}
        />
      )}
    </fieldset>
  );
};

export default CarrierContactsPanel;
